import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { cmdPageQuery, type CmdPane, type CmdPaneKey } from '../api/deviceCmd'
import { Spinner } from '../ui/Spinner'
import './DeviceCmd.css'
import { DeviceCmdCommands } from './DeviceCmdCommands'
import { DeviceCmdTerminal } from './DeviceCmdTerminal'
import type { ScreenProps } from './screens'

/**
 * Remote Command Prompt - opteemum/command_prompt/index.php si filele lui.
 *
 * -----------------------------------------------------------------------------
 * FILELE VIN DE LA SERVER
 * -----------------------------------------------------------------------------
 *
 * Lista de file (`panes`) e scrisa de API, dupa drepturile omului si dupa
 * sistemul masinii: pe Windows Command Prompt Lite / Command Prompt / Power
 * Shell, pe Linux o singura fila Terminal, si Commands in amandoua cazurile.
 * O fila ascunsa in browser nu e o poarta - de-aia capul de trimitere cere
 * INCA O DATA dreptul filei din care vine linia. Vezi App\Devices\Cmd\CmdPanes.
 *
 * -----------------------------------------------------------------------------
 * CE COMENZI SE POT TRIMITE
 * -----------------------------------------------------------------------------
 *
 * Coloana din dreapta terminalului nu e o decoratie: e catalogul din
 * `it_cmd_categories` / `it_cmd_commands`, taiat pe sistemul si pe rolul
 * masinii, iar butoanele lui trimit ID-ul comenzii, nu textul - serverul il
 * rezolva. Linia scrisa de mana pleaca prin `prepareCommand`, care primeste
 * numai `custom` sau unul din cele unsprezece nume stiute din
 * `terminal_helper.php`.
 *
 * Cand masina doarme, filele de terminal nu se deschid deloc: un terminal n-are
 * cu cine vorbi. Ramane Commands, de unde coada ei se poate si curata.
 */

export function DeviceCmd({ id }: ScreenProps) {
  const page = useQuery(cmdPageQuery(id))
  const [chosen, setChosen] = useState<CmdPaneKey | null>(null)

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  if (!page.data.available) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> Remote Command Prompt is available only for
        workstations, laptops and servers.
      </div>
    )
  }

  const asleep = page.data.asleep
  const linux = page.data.linux || page.data.is_linux
  const panes = (page.data.panes ?? []).filter((pane) => !asleep || pane.key === 'commands')

  if (panes.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> You do not have access to any of the terminal panes
        on this machine.
      </div>
    )
  }

  const active = panes.find((pane) => pane.key === chosen) ?? panes[0]

  return (
    <div className="row device-cmd">
      <div className="col-md-12">
        {asleep && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
            <strong>This station is inactive.</strong> A terminal has nobody to talk to, but what is already queued for
            it is below - and can be cleared from there.
          </div>
        )}
        <ul className="nav nav-tabs line-tabs mb-2 cmdTabs" role="tablist">
          {panes.map((pane) => (
            <li key={pane.key} className={pane.key === active.key ? 'active' : undefined}>
              <a
                href={hrefOf(pane, id)}
                onClick={(e) => {
                  e.preventDefault()
                  setChosen(pane.key)
                }}
              >
                <i className={pane.icon}></i> {pane.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {active.key === 'commands' ? (
            <DeviceCmdCommands id={id} />
          ) : (
            <DeviceCmdTerminal
              key={active.key}
              deviceId={id}
              pane={active}
              linux={linux}
              title={titleOf(active, linux)}
              prompt={promptOf(active, linux)}
              greeting={greetingOf(active, linux)}
              sections={page.data.sections ?? []}
              sectionsNote={page.data.sections_note ?? ''}
            />
          )}
          {!active.can_send && active.key !== 'commands' && (
            <p className="color-light-grey cmd-readonly">
              <i className="fas fa-info-circle" aria-hidden="true"></i> You can open this pane, but sending a command
              needs the Command Prompt right.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/* Adresele vechi raman in markup - regula "identic": aceeasi scoica, aceleasi
   legaturi - dar clicul nu mai pleaca din pagina. */
function hrefOf(pane: CmdPane, id: number): string {
  if (pane.key === 'commands') return `/opteemum/deviceCommands/${id}`
  if (pane.key === 'prompt') return `/opteemum/command_prompt/${id}`
  if (pane.key === 'powershell') return `/opteemum/power_shell/${id}`

  return `/opteemum/command_prompt_categories/${id}`
}

function titleOf(pane: CmdPane, linux: boolean): string {
  if (pane.key === 'powershell') return 'Power Shell'
  if (linux) return 'Bash'

  return 'CMD'
}

function promptOf(pane: CmdPane, linux: boolean): string {
  if (pane.key === 'powershell') return 'PS> '
  if (linux) return '/ $ '

  return 'cmd> '
}

function greetingOf(pane: CmdPane, linux: boolean): string {
  if (pane.key === 'powershell') return 'OptimumDesk PowerShell'
  if (linux) return 'OptimumDesk Linux Terminal'

  return 'OptimumDesk Command Prompt'
}
